import Link from 'next/link'
import { z } from 'zod'

import Chip from '@nxt-insurance/ui-kit/components/Chip'

import { webUrlSchema } from '@/cms/webUrl'
import { EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { useUrlFromWebUrlReference } from '@/hooks/useUrlFromWebUrlReference'
import { useWebUrlRel } from '@/hooks/useWebUrlRel'
import type { PortableTextTypeComponentProps } from '@portabletext/react'

const chipRow = z.object({
  _type: z.literal('chipRow'),
  _key: z.string(),
  chips: z.array(
    z.object({
      _key: z.string(),
      label: z.string(),
      target: webUrlSchema,
    })
  ),
})

type ChipRowSchema = z.infer<typeof chipRow>

const ChipRowElement = (props: { sectionName: string; chip: ChipRowSchema['chips'][number] }) => {
  const { track } = useAnalytics()
  const href = useUrlFromWebUrlReference(props.chip.target, { optional: true })
  const rel = useWebUrlRel(props.chip.target)

  return props.chip.target && href ? (
    <Link href={href} rel={rel}>
      <Chip
        label={props.chip.label}
        onPress={() => {
          track(EVENTS.ChipClicked, {
            label: props.chip.label,
            href,
            sectionName: props.sectionName,
          })
        }}
      />
    </Link>
  ) : (
    <Chip
      label={props.chip.label}
      onPress={() => {
        track(EVENTS.ChipClicked, {
          label: props.chip.label,
          sectionName: props.sectionName,
        })
      }}
    />
  )
}

export const ChipsRow = (props: {
  node: PortableTextTypeComponentProps<unknown>
  sectionName: string
}) => {
  const value = chipRow.parse(props.node.value)
  return (
    <div className="flex flex-row flex-wrap gap-5">
      {value.chips.map((chip) => (
        <ChipRowElement chip={chip} sectionName={props.sectionName} key={chip._key} />
      ))}
    </div>
  )
}
